import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../api';
import { useApp } from '../context/AppContext';

const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;
const today = () => new Date().toISOString().split('T')[0];

export default function OrderEntry() {
  const { toast } = useApp();
  const [employees, setEmployees] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [orderDate, setOrderDate] = useState(today());
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [catFilter, setCatFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [empSearch, setEmpSearch] = useState('');
  const [showSuccess, setShowSuccess] = useState(null);

  useEffect(() => {
    api.getEmployees({ active: 'true' }).then(setEmployees);
    api.getItems(true).then(setItems);
  }, []);

  const categories = useMemo(() => ['All', ...new Set(items.map(i => i.category))], [items]);

  const filteredItems = useMemo(() => items.filter(item => {
    const matchCat = catFilter === 'All' || item.category === catFilter;
    const matchSearch = item.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  }), [items, catFilter, search]);

  const filteredEmployees = employees.filter(e =>
    e.name.toLowerCase().includes(empSearch.toLowerCase()) ||
    (e.emp_code || '').toLowerCase().includes(empSearch.toLowerCase())
  );

  const totalAmount = useMemo(() => {
    return Object.entries(quantities).reduce((sum, [itemId, qty]) => {
      const item = items.find(i => i.id === parseInt(itemId));
      return sum + (item ? item.price * (qty || 0) : 0);
    }, 0);
  }, [quantities, items]);

  const orderItemCount = useMemo(() =>
    Object.values(quantities).filter(q => q > 0).length, [quantities]);

  const setQty = (itemId, val) => {
    const n = Math.max(0, parseInt(val) || 0);
    setQuantities(prev => ({ ...prev, [itemId]: n }));
  };

  const increment = (itemId) => setQty(itemId, (quantities[itemId] || 0) + 1);
  const decrement = (itemId) => setQty(itemId, Math.max(0, (quantities[itemId] || 0) - 1));

  const clearOrder = () => {
    setQuantities({});
    setRemarks('');
  };

  const handleSubmit = async () => {
    if (!selectedEmp) return toast('Please select an employee', 'error');
    const itemsToOrder = Object.entries(quantities)
      .filter(([, qty]) => qty > 0)
      .map(([item_id, quantity]) => ({ item_id: parseInt(item_id), quantity }));
    if (!itemsToOrder.length) return toast('Please add at least one item', 'error');

    setSaving(true);
    try {
      const order = await api.createOrder({
        employee_id: selectedEmp.id,
        order_date: orderDate,
        items: itemsToOrder,
        remarks,
      });
      setShowSuccess({ employee: selectedEmp.name, total: order.total_amount, id: order.id });
      clearOrder();
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Employee Panel ─────────────────────────────────── */}
      <div style={{
        width: 240, minWidth: 240, borderRight: '1px solid var(--iocl-border)',
        display: 'flex', flexDirection: 'column', background: 'var(--iocl-panel)'
      }}>
        <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--iocl-border)' }}>
          <div style={{ fontSize: 11, color: 'var(--iocl-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
            Select Employee
          </div>
          <div className="search-input-wrap">
            <span className="search-icon">🔍</span>
            <input
              placeholder="Search name..."
              value={empSearch}
              onChange={e => setEmpSearch(e.target.value)}
            />
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {filteredEmployees.map(emp => (
            <div
              key={emp.id}
              onClick={() => { setSelectedEmp(emp); clearOrder(); setShowSuccess(null); }}
              style={{
                padding: '10px 14px',
                cursor: 'pointer',
                borderLeft: `3px solid ${selectedEmp?.id === emp.id ? 'var(--iocl-orange)' : 'transparent'}`,
                background: selectedEmp?.id === emp.id ? 'var(--iocl-slate)' : 'transparent',
                transition: 'all 0.12s',
              }}
            >
              <div style={{ fontSize: 13.5, color: selectedEmp?.id === emp.id ? 'var(--iocl-orange)' : 'var(--iocl-white)' }}>
                {emp.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--iocl-muted)', marginTop: 2 }}>
                {emp.emp_code} · {emp.department_name}
              </div>
            </div>
          ))}
          {filteredEmployees.length === 0 && (
            <div className="empty-state" style={{ padding: 24 }}>
              <p>No employees found</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Order Panel ───────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {!selectedEmp ? (
          <div className="loading-center" style={{ flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 40 }}>👈</div>
            <div style={{ color: 'var(--iocl-muted)' }}>Select an employee to start an order</div>
          </div>
        ) : (
          <>
            {/* Employee header */}
            <div style={{
              padding: '14px 20px',
              borderBottom: '1px solid var(--iocl-border)',
              background: 'var(--iocl-slate)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ fontFamily: 'Rajdhani', fontSize: 20, fontWeight: 700 }}>
                  {selectedEmp.name}
                </div>
                <div style={{ fontSize: 12, color: 'var(--iocl-muted)' }}>
                  {selectedEmp.emp_code} · {selectedEmp.department_name}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 10, marginBottom: 4 }}>Order Date</label>
                  <input
                    type="date"
                    value={orderDate}
                    max={today()}
                    onChange={e => setOrderDate(e.target.value)}
                    style={{ width: 'auto', padding: '6px 10px', fontSize: 13 }}
                  />
                </div>
                <button className="btn btn-secondary btn-sm" onClick={clearOrder}>
                  ✕ Clear
                </button>
              </div>
            </div>

            {/* Success banner */}
            {showSuccess && (
              <div style={{
                background: '#064e3b', border: '1px solid #065f46', padding: '12px 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                flexShrink: 0,
              }}>
                <div style={{ color: '#6ee7b7' }}>
                  ✅ Order #{showSuccess.id} saved for <strong>{showSuccess.employee}</strong> — {fmt(showSuccess.total)}
                </div>
                <button
                  onClick={() => setShowSuccess(null)}
                  style={{ background: 'none', border: 'none', color: '#6ee7b7', cursor: 'pointer', fontSize: 16 }}
                >×</button>
              </div>
            )}

            {/* Filters */}
            <div style={{ padding: '12px 20px 0', background: 'var(--iocl-slate)', borderBottom: '1px solid var(--iocl-border)', flexShrink: 0 }}>
              <div className="search-input-wrap" style={{ marginBottom: 10 }}>
                <span className="search-icon">🔍</span>
                <input
                  placeholder="Search menu items..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="cat-tabs" style={{ paddingBottom: 12 }}>
                {categories.map(cat => (
                  <button
                    key={cat}
                    className={`cat-tab ${catFilter === cat ? 'active' : ''}`}
                    onClick={() => setCatFilter(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Items list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
              {filteredItems.length === 0 ? (
                <div className="empty-state"><p>No items found</p></div>
              ) : (
                filteredItems.map(item => {
                  const qty = quantities[item.id] || 0;
                  const subtotal = qty * item.price;
                  return (
                    <div key={item.id} className="item-row">
                      <div>
                        <div className="item-name">{item.name}</div>
                        <div className="item-category">{item.category} · {item.unit}</div>
                      </div>
                      <div className="item-price">₹{item.price}</div>
                      <div className="counter-wrap">
                        <button className="counter-btn" onClick={() => decrement(item.id)}>−</button>
                        <input
                          type="number"
                          className="counter-input"
                          value={qty || ''}
                          placeholder="0"
                          min="0"
                          onChange={e => setQty(item.id, e.target.value)}
                        />
                        <button className="counter-btn plus" onClick={() => increment(item.id)}>+</button>
                      </div>
                      <div className="item-subtotal">
                        {subtotal > 0 ? fmt(subtotal) : <span style={{ color: 'var(--iocl-border)' }}>—</span>}
                      </div>
                    </div>
                  );
                })
              )}
              {remarks !== undefined && (
                <div style={{ padding: '14px 0 20px' }}>
                  <label>Remarks (optional)</label>
                  <input
                    placeholder="e.g. Extra roti, No onion..."
                    value={remarks}
                    onChange={e => setRemarks(e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* Total bar */}
            <div className="total-bar">
              <div>
                <div className="total-label">
                  TOTAL AMOUNT
                  {orderItemCount > 0 && (
                    <span style={{ fontSize: 12, color: 'var(--iocl-muted)', marginLeft: 10, fontFamily: 'Inter' }}>
                      ({orderItemCount} item{orderItemCount > 1 ? 's' : ''})
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <div className="total-amount">{fmt(totalAmount)}</div>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={saving || orderItemCount === 0}
                  style={{ padding: '10px 24px', fontSize: 14 }}
                >
                  {saving ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving...</> : '✓ Save Order'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
